import { makeAutoObservable } from "mobx";
import { delay } from "src/common/delay";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import { MultiStepTaskStore } from "src/components/MultiStepTask/MultiStepTaskStore";
import { multiStepOnRowClick } from "src/components/MultiStepTask/utils";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import { ZUid } from "src/types/zUid";
import { TableStore } from "src/components/tables/TableStore";
import { singleLoader } from "src/components/tables/singleLoader";
import {
  copyBoms2aToEntity,
  loadBoms2a,
} from "src/businessServices/services/bsBom2a/apiBom2a";
import { ZBom2a } from "src/businessServices/services/bsBom2a/Bom2aTypes";
import { Bom2aPositionsStore } from "../../Bom2aPositionsStore";

export class Bom2aPosCopyFromStore {
  constructor(
    public readonly modelObjectId: ZUid,
    public readonly objectServiceId: ZUid,
    public readonly targetBomId: ZUid,
  ) {
    this.store1 = new EntityFiltersPageStore({
      objectId: this.modelObjectId,
      selectionSettings: { selectionType: "radio" },
      onRowClick: (row) => multiStepOnRowClick(row, this.store1),
    });
    makeAutoObservable(this);
  }

  taskStore = new MultiStepTaskStore();

  async init() {
    this.taskStore.open();
  }

  // --- step 1
  store1: EntityFiltersPageStore;

  get srcModelId(): ZUid | undefined {
    return this.store1.tableStore?.selected[0]?.id;
  }

  get firstDisabled() {
    return !this.bomsStore;
  }

  // --- step 2
  get bomsStore() {
    const { srcModelId, objectServiceId } = this;
    if (!srcModelId) return null;
    return new TableStore<ZBom2a, {}>({
      rowKey: "id",
      fnLoad: singleLoader(
        () => loadBoms2a({ objectServiceId, entityId: srcModelId }),
        () => true,
      ),
      selectionSettings: {
        keepSelected: true,
        selectionType: "radio",
      },
    });
  }

  get srcBom() {
    return this.bomsStore?.selected[0];
  }

  get secondDisabled() {
    return !this.srcBom;
  }

  // ---- step 3
  onPosActive() {
    const { srcBom } = this;
    if (srcBom) {
      this.posStore = new Bom2aPositionsStore();
      this.posStore.init(srcBom).catch(onError);
    }
  }

  posStore: Bom2aPositionsStore | null = null;

  get selectedPosIds(): ZUid[] {
    return this.posStore?.selectedList ?? [];
  }

  get thirdDisabled() {
    return !this.posStore?.selectedIds.size;
  }

  // --- copy
  copyInfo: RemoteData<boolean> = { status: "none" };

  setCopyInfo(data: RemoteData<boolean>) {
    this.copyInfo = data;
  }

  async copy(onFinish: () => void) {
    try {
      this.setCopyInfo({ status: "wait" });
      const { selectedPosIds } = this;
      if (selectedPosIds.length === 0) {
        throw Error("Empty positions list");
      }
      await copyBoms2aToEntity(selectedPosIds, this.targetBomId);
      this.setCopyInfo({ status: "ready", result: true });
      onFinish();
      await delay(500); // Задержка, чтобы показать сообщение об успехе.
      this.taskStore.close();
    } catch (error) {
      this.setCopyInfo({ status: "error", error });
    }
  }
}
